import { ReactNode, useEffect, useMemo } from 'react';

// material-ui
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { createTheme, Theme, ThemeOptions, ThemeProvider, TypographyVariantsOptions } from '@mui/material/styles';

// project import
import useConfigContext from "context/useConfigContext";
import Palette from './palette';
import Typography from './typography';
import CustomShadows from './shadows';
import componentsOverride from './overrides';

// types
import { CustomShadowProps } from "./types/ThemeType";
import useGlobalContext from "../../context/useGlobalContext";
import { FontFamily, MenuOrientation, PresetColor, ThemeDirection, ThemeMode } from "../../types/ConfigType";

// types
type ThemeCustomizationProps = {
    children: ReactNode;
};

// ==============================|| DEFAULT THEME - MAIN  ||============================== //

export default function ThemeCustomization({ children }: ThemeCustomizationProps) {
    const { themeDirection, mode, presetColor, fontFamily, menuOrientation, miniDrawer, container,

        onChangeContainer,
        onChangeLocalization,
        onChangeMode,
        onChangePresetColor,
        onChangeDirection,
        onChangeMiniDrawer,
        onChangeMenuOrientation,
        onChangeFontFamily
    } = useConfigContext();
    const { companyTheme } = useGlobalContext()

    useEffect(() => {

        if (companyTheme) {
            let companyThemeObj = companyTheme?.value?.general_theme;


            if (companyThemeObj) {
                if (fontFamily != companyThemeObj.fontFamily) {
                    onChangeFontFamily(companyThemeObj.fontFamily as FontFamily)
                }
                if (menuOrientation != companyThemeObj.menuOrientation) {
                    onChangeMenuOrientation(companyThemeObj.menuOrientation as MenuOrientation)
                }
                if (miniDrawer != companyThemeObj.miniDrawer) {
                    onChangeMiniDrawer(companyThemeObj.miniDrawer as boolean)
                }
                // if (container != companyThemeObj.container) {
                //     onChangeContainer(companyThemeObj.container)
                // }
                if (mode != companyThemeObj.mode) {
                    onChangeMode(companyThemeObj.mode as ThemeMode)
                }
                if (presetColor != companyThemeObj.presetColor) {
                    onChangePresetColor(companyThemeObj.presetColor as PresetColor)
                }
                if (themeDirection != companyThemeObj.themeDirection) {
                    onChangeDirection(companyThemeObj.themeDirection as ThemeDirection)
                }

            }
        }
    }, [companyTheme]);


    const theme: Theme = useMemo<Theme>(() => Palette(mode, presetColor), [mode, presetColor]);

    const themeTypography: TypographyVariantsOptions = useMemo<TypographyVariantsOptions>(
        () => Typography(mode, fontFamily, theme),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mode, fontFamily]
    );
    const themeCustomShadows: CustomShadowProps = useMemo<CustomShadowProps>(() => CustomShadows(theme), [theme]);

    const themeOptions: ThemeOptions = useMemo(
        () => ({
            breakpoints: {
                values: {
                    xs: 0,
                    sm: 768,
                    md: 1024,
                    lg: 1266,
                    xl: 1440
                }
            },
            direction: themeDirection,
            mixins: {
                toolbar: {
                    minHeight: 60,
                    paddingTop: 8,
                    paddingBottom: 8
                }
            },
            palette: theme.palette,
            customShadows: themeCustomShadows,
            typography: themeTypography
        }),
        [themeDirection, theme, themeTypography, themeCustomShadows]
    );

    const themes: Theme = createTheme(themeOptions);
    themes.components = componentsOverride(themes);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={themes}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </StyledEngineProvider>
    );
}
